import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/site";
import { CITIES } from "@/lib/locations";
import Logo from "./Logo";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy-900 text-white">
      <div className="container-x py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-2 max-w-md">
          <Logo variant="light" />
          <p className="mt-6 text-white/70 leading-relaxed">
            Compassionate, concierge-level in-home care for seniors and families
            across {BRAND.serviceArea}. Built on family values — delivered with
            uncompromising professionalism.
          </p>
          <Link
            to="/contact"
            data-testid="footer-cta-consultation"
            className="btn-primary mt-8"
          >
            Schedule a Free Consultation
          </Link>
        </div>

        <div>
          <h4 className="text-white font-serif text-xl mb-5">Explore</h4>
          <ul className="space-y-3 text-white/70">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-${l.testid}`}
                  className="hover:text-gold-300 transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-serif text-xl mb-5">Get in touch</h4>
          <ul className="space-y-4 text-white/80">
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 mt-1 text-gold-400" strokeWidth={1.75} />
              <a
                href={BRAND.phoneHref}
                data-testid="footer-phone-link"
                className="hover:text-gold-300 transition-colors"
              >
                {BRAND.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-1 text-gold-400" strokeWidth={1.75} />
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email-link"
                className="hover:text-gold-300 transition-colors"
              >
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-1 text-gold-400" strokeWidth={1.75} />
              <span>{BRAND.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="h-4 w-4 mt-1 text-gold-400" strokeWidth={1.75} />
              <span>{BRAND.hours}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Cities strip — big local-SEO win */}
      <div className="border-t border-white/10">
        <div className="container-x py-10">
          <div className="text-xs uppercase tracking-[0.24em] text-gold-300 mb-5">
            Serving Southwest Florida
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/home-care/${c.slug}`}
                data-testid={`footer-city-${c.slug}`}
                className="text-white/70 hover:text-gold-300 transition-colors"
              >
                Home Care in {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <p>
            © {new Date().getFullYear()} {BRAND.fullName}. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/directory" data-testid="footer-directory" className="hover:text-gold-300 transition-colors">
              Site Directory
            </Link>
            <p className="italic font-serif text-white/60">
              "We treat your loved ones like our own."
            </p>
          </div>
        </div>
        <div className="container-x pb-6 -mt-2 text-center md:text-right">
          <p className="text-xs text-white/40 tracking-wide">
            Website created by{" "}
            <a
              href="https://Jkmediacompany.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-dev-credit"
              className="text-white/60 hover:text-gold-300 hover:underline underline-offset-4 transition-colors"
            >
              JK DIGITAL MEDIA SOLUTIONS, LLC
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
